import { useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  getInstallState,
  initializeAppInstallCapture,
  subscribeToInstallState,
} from "../services/appInstallService";
import {
  appInstallEventKey,
  currentInstallIdentity,
  recordAppInstallEvent,
} from "../services/appInstallAnalytics";

const STAFF_WORKSPACE_PREFIXES = [
  "/admin",
  "/marketing-admin",
  "/finance-admin",
  "/operations-admin",
  "/promoter",
];

const isStaffWorkspace = () =>
  typeof window !== "undefined" &&
  STAFF_WORKSPACE_PREFIXES.some((prefix) =>
    window.location.pathname.startsWith(prefix),
  );

const safelyRecord = async (eventType, eventKey, extra = {}) => {
  try {
    await recordAppInstallEvent(eventType, {
      eventKey,
      ...extra,
    });
  } catch (error) {
    console.warn("UniAir app-install analytics failed:", error);
  }
};

export default function AppInstallTracker() {
  const { user } = useAuth();

  useEffect(() => {
    initializeAppInstallCapture();
  }, []);

  useEffect(() => {
    if (
      !user?.id ||
      user?.user_type !== "client" ||
      isStaffWorkspace()
    ) {
      return undefined;
    }

    const { installId, sessionId } = currentInstallIdentity();

    const recordCurrentState = () => {
      const state = getInstallState();

      if (state.canPrompt) {
        void safelyRecord(
          "install_prompt_available",
          appInstallEventKey.promptAvailable(installId),
        );
      }

      if (state.installedThisSession) {
        void safelyRecord(
          "appinstalled",
          appInstallEventKey.appInstalled(installId),
        );
      }

      if (state.standalone) {
        void safelyRecord(
          "standalone_launch",
          appInstallEventKey.standaloneLaunch(installId, sessionId),
        );
      }
    };

    recordCurrentState();

    return subscribeToInstallState((detail) => {
      const kind = detail?.kind;
      const state = detail?.state || getInstallState();

      if (kind === "prompt_available" && state.canPrompt) {
        void safelyRecord(
          "install_prompt_available",
          appInstallEventKey.promptAvailable(installId),
        );
      }

      if (kind === "appinstalled" || state.installedThisSession) {
        void safelyRecord(
          "appinstalled",
          appInstallEventKey.appInstalled(installId),
        );
      }

      if (state.standalone) {
        void safelyRecord(
          "standalone_launch",
          appInstallEventKey.standaloneLaunch(installId, sessionId),
        );
      }
    });
  }, [user?.id, user?.user_type]);

  return null;
}
